/**
 * Created: 2026-04-08
 * Initial commit: Split SlotsPage into SlotsPage/ directory (feat/event/mr-10)
 * Updated: 2026-04-12 — replaced DataGridPro with pure CSS Grid (feat/event/mr-14)
 *
 * Pure CSS Grid slot table. Replaces the previous DataGridPro implementation.
 * Each data cell carries data-hour and data-court-id attributes so that
 * useDragSelection can identify cells without MUI internals.
 */

import { CircularProgress, Tooltip } from '@mui/material'
import { Fragment } from 'react'
import { SlotStatus } from '../../gql/graphql'
import styles from './SlotGrid.module.css'
import type { SlotMeta } from './types'

function formatHour(h: number) {
  return `${h.toString().padStart(2, '0')}:00`
}

interface Court {
  id: string
  name: string
  displayOrder: number
}

interface SlotGridProps {
  courts: Court[]
  /** Ordered list of hours to render, derived from the slots query response */
  hours: number[]
  slotLookup: Map<string, SlotMeta>
  /** Cells covered by a span block — rendered by the span layer instead */
  coveredCells: Set<string>
  /** Span blocks to render as merged cells */
  spanBlocks: SpanBlock[]
  pastHourThreshold: number
  selectedKeys: Set<string>
  loading: boolean
  gridRef: React.RefObject<HTMLDivElement | null>
  onMouseDown: (e: React.MouseEvent) => void
  onMouseMove: (e: React.MouseEvent) => void
  onMouseUp: (e: React.MouseEvent) => void
  onCellClick: (hour: number, courtId: string) => void
}

export interface SpanBlock {
  /** 0-based index into courts array */
  courtIndex: number
  hourStart: number
  rowSpan: number
  colSpan: number
  /** representative slot (top-left cell) */
  meta: SlotMeta
}

export default function SlotGrid({
  courts,
  hours,
  slotLookup,
  coveredCells,
  spanBlocks,
  pastHourThreshold,
  selectedKeys,
  loading,
  gridRef,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onCellClick,
}: SlotGridProps) {
  // CSS Grid: 1 time col + 1 col per court, each 100px wide
  const gridTemplateColumns = `80px repeat(${courts.length}, 100px)`

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drag-selection container needs mouse events
    <div
      ref={gridRef}
      className={styles.wrapper}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >
      {loading && (
        <div className={styles.loadingOverlay}>
          <CircularProgress />
        </div>
      )}

      <div className={styles.grid} style={{ gridTemplateColumns }}>
        {/* ── Header row ── */}
        <div className={styles.headerCell}>时段</div>
        {courts.map((c) => (
          <div key={c.id} className={styles.headerCell}>
            {c.name}
          </div>
        ))}

        {/* ── Span blocks (merged cells) ── */}
        {spanBlocks.map((block) => {
          // grid rows: header=1, first hour → row 2, nth hour → row n+2
          const gridRowStart = hours.indexOf(block.hourStart) + 2
          const gridColStart = block.courtIndex + 2 // time col is col 1
          const isClickable =
            !!block.meta.eventId ||
            (block.meta.status === SlotStatus.Booked && !!block.meta.orderId)
          const statusClass =
            block.meta.status === SlotStatus.Booked
              ? styles.statusBooked
              : block.meta.status === SlotStatus.Locked
                ? styles.statusLocked
                : styles.statusReserved
          return (
            // biome-ignore lint/a11y/noStaticElementInteractions: grid cell is conditionally interactive
            <div
              key={`span-${block.courtIndex}-${block.hourStart}`}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              className={`${styles.cell} ${styles.span} ${statusClass}`}
              style={{
                gridRow: `${gridRowStart} / span ${block.rowSpan}`,
                gridColumn: `${gridColStart} / span ${block.colSpan}`,
                height: 'auto',
                cursor: isClickable ? 'pointer' : undefined,
              }}
              data-hour={block.hourStart}
              data-court-id={courts[block.courtIndex].id}
              onClick={() => {
                if (isClickable) {
                  onCellClick(block.hourStart, courts[block.courtIndex].id)
                }
              }}
              onKeyDown={(e) => {
                if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                  onCellClick(block.hourStart, courts[block.courtIndex].id)
                }
              }}
            >
              <SlotCellContent meta={block.meta} />
            </div>
          )
        })}

        {/* ── Data rows ── */}
        {hours.map((hour) => (
          <Fragment key={hour}>
            {/* Time label */}
            <div className={styles.timeCell}>{formatHour(hour)}</div>

            {/* Court cells */}
            {courts.map((court) => {
              const key = `${hour}-${court.id}`
              if (coveredCells.has(key)) return null

              const meta = slotLookup.get(key)
              const isPast = hour < pastHourThreshold
              const isUnavailable = !meta

              const classNames = [styles.cell]
              if (isPast) classNames.push(styles.past)
              else if (isUnavailable) classNames.push(styles.unavailable)
              else {
                if (meta.status === SlotStatus.Reserved)
                  classNames.push(styles.statusReserved)
                else if (meta.status === SlotStatus.Booked)
                  classNames.push(styles.statusBooked)
                else if (meta.status === SlotStatus.Locked)
                  classNames.push(styles.statusLocked)
              }
              if (selectedKeys.has(key)) classNames.push(styles.selected)

              const isClickable = !isPast && !!meta
              return (
                // biome-ignore lint/a11y/noStaticElementInteractions: grid cell is conditionally interactive
                <div
                  key={key}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  className={classNames.join(' ')}
                  data-hour={hour}
                  data-court-id={court.id}
                  onClick={() => {
                    if (isClickable) onCellClick(hour, court.id)
                  }}
                  onKeyDown={(e) => {
                    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                      onCellClick(hour, court.id)
                    }
                  }}
                >
                  {meta && !isPast && <SlotCellContent meta={meta} />}
                </div>
              )
            })}
          </Fragment>
        ))}
      </div>
    </div>
  )
}

/** Render the content of a slot cell (price + optional reason label) */
export function SlotCellContent({ meta }: { meta: SlotMeta }) {
  if (meta.reason) {
    return (
      <Tooltip
        title={meta.reason}
        arrow
        placement="top"
        slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
      >
        <div style={{ width: '100%' }}>
          <div className={styles.cellPrice}>¥{meta.price}</div>
          <div className={styles.cellReason}>{meta.reason.split('\n')[0]}</div>
        </div>
      </Tooltip>
    )
  }
  return <div className={styles.cellPrice}>¥{meta.price}</div>
}
